import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Play } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EducationPage() {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState('all');
  const categories = ['all', 'beginner', 'guide', 'market-update', 'tax', 'general'];

  useEffect(() => {
    const params = filter !== 'all' ? { category: filter } : {};
    axios.get(`${API}/articles`, { params }).then(r => setArticles(r.data)).catch(() => {});
  }, [filter]);

  return (
    <div data-testid="education-page">
      <SEO
        title="Learn About Precious Metals"
        description="Education hub for gold and silver investors in New Zealand. Guides on buying bullion, market updates, storage tips, and tax considerations for NZ investors."
        path="/education"
      />
      <section className="bg-[#0A0A0A] py-16 text-center">
        <div className="max-w-7xl mx-auto px-6">
          <p className="section-label mb-3">Education Hub</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "Cinzel, serif" }}>Learn About Precious Metals</h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Real advice for real people. Explore our guides and market updates written for everyday New Zealand investors.
          </p>
        </div>
      </section>

      <section className="py-12 max-w-7xl mx-auto px-6 md:px-12">
        {/* Bullion Glossary highlighted card */}
        <Link to="/education/glossary" className="group block mb-10 relative overflow-hidden bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-sm p-6 md:p-8 hover:border-[#D4AF37] transition-all" data-testid="glossary-cta-card">
          <div className="relative flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
              <BookOpen size={26} className="text-[#D4AF37]" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>Reference</p>
              <h2 className="text-xl md:text-2xl font-bold text-white group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: "Cinzel, serif" }}>Bullion Glossary</h2>
              <p className="text-sm text-gray-400 mt-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Plain-English definitions from AVDP&nbsp;oz to Zinc — including the difference between troy and AVDP ounces.
              </p>
            </div>
            <span className="text-[#D4AF37] font-semibold text-sm inline-flex items-center gap-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Browse A–Z <ArrowRight size={14} />
            </span>
          </div>
        </Link>

        <div className="flex gap-2 mb-8 flex-wrap">
          {categories.map(c => (
            <button key={c} onClick={() => setFilter(c)} className={`px-4 py-2 text-sm rounded-sm capitalize transition-all ${filter === c ? 'bg-[#0A0A0A] text-[#D4AF37]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={{ fontFamily: "'Poppins', sans-serif" }} data-testid={`edu-filter-${c}`}>
              {c.replace('-', ' ')}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map(article => (
            <Link key={article.slug} to={`/education/${article.slug}`} className="group product-card border border-gray-200 rounded-sm overflow-hidden bg-white" data-testid={`edu-article-${article.slug}`}>
              <div className="aspect-[16/10] overflow-hidden bg-gray-50 relative">
                {article.external_video_url && article.image_url ? (
                  <>
                    <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center">
                        <Play size={22} className="text-[#0A0A0A] ml-0.5" fill="#0A0A0A" />
                      </div>
                    </div>
                  </>
                ) : article.video_url ? (
                  <video src={article.video_url} poster={article.video_url.replace('.mp4', '-poster.jpg')} className="w-full h-full object-cover" muted loop autoPlay playsInline />
                ) : article.image_url ? (
                  <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100"><BookOpen size={48} className="text-gray-300" /></div>
                )}
              </div>
              <div className="p-6">
                <span className="text-xs uppercase tracking-wider text-[#D4AF37] font-semibold" style={{ fontFamily: "'Poppins', sans-serif" }}>{article.category}</span>
                <h3 className="text-lg font-semibold mt-1 mb-2 group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: "'Poppins', sans-serif" }}>{article.title}</h3>
                <p className="text-sm text-gray-500 mb-4 line-clamp-3" style={{ fontFamily: "'Poppins', sans-serif" }}>{article.excerpt}</p>
                <span className="text-sm text-[#D4AF37] font-semibold inline-flex items-center gap-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Read More <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
        {articles.length === 0 && (
          <p className="text-center text-gray-400 py-12">No articles found. Check back soon for new content.</p>
        )}
      </section>
    </div>
  );
}
